import {motion} from 'framer-motion'
import html2pdf from 'html2pdf.js'
import moment from 'moment'
import {Badge} from 'primereact/badge'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Sidebar} from 'primereact/sidebar'
import {Tag} from 'primereact/tag'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import {useHistory} from 'react-router-dom/cjs/react-router-dom'
import TranslateUtils from '../../i18n/TranslateUtils'
import FormHeader from '../form/FormHeader'
import ToolBoxWrapper from '../layout/ToolBoxWrapper'
import {showLoading, stopLoading} from '../loading/LoadingService'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {
    anySupplementaryOxygenOptions,
    bloodPressureLimbOptions,
    bloodPressurePositionOptions,
    consciousnessOptions,
} from '../observations/Constants'
import {
    accommodationStatusList,
    diagnosticList,
    identityWithHeldReasonList,
    materialStatusList,
    patientKeys,
    religionList,
    titleList,
} from '../patient/PatientConstants'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import PdfContent from './PdfContent'
import {dataColumns} from './ReportConstanst'
const types = {
    csv: 'CSV',
    pdf: 'PDF',
    excel: 'Excel',
}

const excludeUserData = [
    'id',
    'clientId',
    'hospitalId',
    'additionalInfoId',
    'addressId',
    'addressConfig',
    'pathwayIds',
    'pathways',
    'healthRecords',
    'hcpIds',
    'abnormality',
    'phoneExtension',
    'tags',
    '@timestamp',
    '@version',
    'initialsRedirectUrl',
]

const excludeHealthData = ['initialsRedirectUrl']
const ReportTableResult = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const [visible, setVisible] = useState(false)
    const [excludesColumnAdvance, setExcludeColumnAdvance] = useState([])
    const {
        translateCommons,
        translateDatatable,
        translateKeys,
        translateOptions,
    } = TranslateUtils()
    const editColumnModalSheetRef = useRef(null)
    const datatableRef = useRef(null)

    const history = useHistory()

    const pdfElement = useRef(null)
    const [queryData, setQueryData] = useState([])
    const [isReviewPDF, setIsReviewPDF] = useState(false)
    const [pdfData, setPdfData] = useState({})

    // Ref handle
    const handleShowForm = data => {
        setQueryData([...data])
        setVisible(true)
    }

    const handleHideForm = () => {
        setQueryData([])
        setVisible(false)
    }

    // Compose labels header of table
    const dataLabelColumns = useMemo(() => {
        const excludeColumns = [
            ...excludesColumnAdvance,
            'tag',
            '@timestamp',
            '@version',
        ]

        let dataList = [...dataColumns]

        dataList = dataList.filter(item => !excludeColumns.includes(item?.key))

        let dataLabelColumns = dataList?.map(x => ({
            ...x,
            field: x.key,
            header: convertKeyToLabel(x.key),
        }))
        return dataLabelColumns
    }, [excludesColumnAdvance])

    const automaticWidthSettingExcel = data => {
        let objectMaxLength = []
        if (data?.length > 0) {
            for (let i = 0; i < data.length; i++) {
                let value = Object.values(data[i])
                let key = Object.keys(data[i])

                if (value?.length > 0) {
                    for (let j = 0; j < value.length; j++) {
                        let maxLength = 10

                        if (typeof value[j] == 'number' || !value[j]) {
                            objectMaxLength[j] = key[j].length + 2 || maxLength
                        } else {
                            if (value[j]?.length >= key[j].length) {
                                maxLength = value[j]?.length
                            } else {
                                if (key[j] && convertKeyToLabel(key[j])) {
                                    maxLength = convertKeyToLabel(
                                        key[j],
                                    )?.length
                                }
                            }

                            if (
                                !objectMaxLength[j] ||
                                maxLength >= objectMaxLength[j]
                            ) {
                                objectMaxLength[j] = maxLength + 2
                            }
                        }
                    }
                }
            }
            if (objectMaxLength?.length > 0) {
                let wscols = objectMaxLength.map(item => {
                    return {width: item}
                })

                return wscols
            }

            return []
        } else {
            return []
        }
    }

    const convertDataToExport = (key, value) => {
        let content = '-'

        if (!value) {
            return '-'
        }

        switch (key) {
            case patientKeys.title:
                content =
                    titleList.find(title => title.value === value)?.label || '-'
                break
            case patientKeys.gender || 'type':
                content = convertKeyToLabel(value)
                break
            case patientKeys.identityWithHeldReason:
                content =
                    translateOptions(identityWithHeldReasonList).find(
                        identity => identity.value === value,
                    )?.label || '-'
                break
            case patientKeys.religion:
                content =
                    translateOptions(religionList).find(
                        religion => religion.value === value,
                    )?.label || '-'
                break
            case patientKeys.maritalStatus:
                content =
                    translateOptions(materialStatusList).find(
                        marital => marital.value === value,
                    )?.label || '-'
                break
            case patientKeys.accommodationStatus:
                content =
                    translateOptions(accommodationStatusList).find(
                        accommodation => accommodation.value === value,
                    )?.label || '-'
                break
            case patientKeys.diagnostic:
                content =
                    translateOptions(diagnosticList).find(
                        diagnostic => diagnostic.value === value,
                    )?.label || '-'
                break
            case 'bloodPressurePosition':
                content =
                    translateOptions(bloodPressurePositionOptions).find(
                        blood => blood.value === value,
                    )?.label || '-'
                break
            case 'anySupplementaryOxygen':
                content =
                    translateOptions(anySupplementaryOxygenOptions).find(
                        supOxygen => supOxygen.value === value,
                    )?.label || '-'
                break
            case 'bloodPressureLimb':
                content =
                    translateOptions(bloodPressureLimbOptions).find(
                        bloodPressure => bloodPressure.value === value,
                    )?.label || '-'
                break
            case 'levelOfConsciousness':
                content =
                    translateOptions(consciousnessOptions).find(
                        consciousness => consciousness.value === value,
                    )?.label || '-'
                break
            default:
                content = value
                break
        }

        return content
    }

    const getFilteredDataForExport = data => {
        // Filter data columns
        let tempDataColumns = data && data?.length > 0 ? [...data] : []
        let filteredColumnList = []

        if (tempDataColumns.length > 0) {
            for (let i = 0; i < tempDataColumns?.length; i++) {
                filteredColumnList.push(
                    Object.keys(tempDataColumns[i]).reduce((obj, key) => {
                        obj[key] =
                            convertDataToExport(key, tempDataColumns[i][key]) ||
                            '-'
                        return obj
                    }, {}),
                )
            }
        }

        const headerFormat = []

        if (filteredColumnList && filteredColumnList?.length > 0) {
            for (const key of Object.keys(filteredColumnList[0])) {
                headerFormat.push(convertKeyToLabel(key))
            }
        }

        return {
            filteredColumnList: filteredColumnList,
            headerFormat: headerFormat,
        }
    }

    const onExport = async (type, data) => {
        showLoading('Saving File...')

        if (!!data) {
            // Mapping Data use for Excel and CSV
            let userData = {...data}

            let healthRecordsData = [...data?.healthRecords]
            let pathwaysData = [
                ...data?.pathways?.map(item => {
                    return {name: item?.name}
                }),
            ]

            excludeUserData.forEach(item => delete userData[item])
            excludeHealthData.forEach(item =>
                healthRecordsData.forEach(health => delete health[item]),
            )

            if (type === types.excel) {
                import('xlsx').then(xlsx => {
                    const wb = xlsx.utils.book_new()

                    let headerText = `Exported at ${moment().toString()}`
                    let headerRow = {A1: {t: 's', v: headerText}}

                    // User Sheet
                    let userExportData = getFilteredDataForExport([userData])
                    const worksheetUser = xlsx.utils.json_to_sheet([])

                    Object.assign(worksheetUser, headerRow)

                    xlsx.utils.sheet_add_aoa(
                        worksheetUser,
                        [userExportData?.headerFormat],
                        {
                            origin: 'A3',
                        },
                    )

                    xlsx.utils.sheet_add_json(
                        worksheetUser,
                        userExportData?.filteredColumnList,
                        {
                            origin: 'A4',
                            skipHeader: true,
                        },
                    )

                    worksheetUser['!cols'] = automaticWidthSettingExcel(
                        userExportData?.filteredColumnList,
                    )

                    xlsx.utils.book_append_sheet(
                        wb,
                        worksheetUser,
                        translateCommons('User Information'),
                    )

                    // Health Records Sheet
                    let healthExportData =
                        getFilteredDataForExport(healthRecordsData)
                    const worksheetHealthRecords = xlsx.utils.json_to_sheet([])

                    Object.assign(worksheetHealthRecords, headerRow)

                    xlsx.utils.sheet_add_aoa(
                        worksheetHealthRecords,
                        [healthExportData?.headerFormat],
                        {
                            origin: 'A3',
                        },
                    )

                    xlsx.utils.sheet_add_json(
                        worksheetHealthRecords,
                        healthExportData?.filteredColumnList,
                        {
                            origin: 'A4',
                            skipHeader: true,
                        },
                    )

                    worksheetUser['!cols'] = automaticWidthSettingExcel(
                        healthExportData?.filteredColumnList,
                    )

                    xlsx.utils.book_append_sheet(
                        wb,
                        worksheetHealthRecords,
                        translateCommons('Health Records'),
                    )

                    // Pathways Sheet
                    let pathwayExportData =
                        getFilteredDataForExport(pathwaysData)
                    const worksheetPathway = xlsx.utils.json_to_sheet([])

                    Object.assign(worksheetPathway, headerRow)

                    xlsx.utils.sheet_add_aoa(
                        worksheetPathway,
                        [pathwayExportData?.headerFormat],
                        {
                            origin: 'A3',
                        },
                    )

                    xlsx.utils.sheet_add_json(
                        worksheetPathway,
                        pathwayExportData?.filteredColumnList,
                        {
                            origin: 'A4',
                            skipHeader: true,
                        },
                    )

                    worksheetUser['!cols'] = automaticWidthSettingExcel(
                        pathwayExportData?.filteredColumnList,
                    )

                    xlsx.utils.book_append_sheet(
                        wb,
                        worksheetPathway,
                        translateCommons('Pathways'),
                    )

                    // Write File
                    xlsx.writeFile(
                        wb,
                        `PatientReport_${userData?.firstName}${userData?.lastName}.xlsx`,
                    )
                })
            }

            if (type === types.pdf) {
                setPdfData(data)
                setIsReviewPDF(true)
            }
        }
        stopLoading()
    }

    const handleOnPrintPDF = useCallback(() => {
        if (pdfData) {
            showNotification(
                severityNotification.info,
                'Preparing PDF file for download...',
            )
            const element = pdfElement.current

            html2pdf()
                .set({
                    filename: `PatientReport_${pdfData?.firstName}${pdfData?.lastName}.pdf`,
                    image: {type: 'jpeg', quality: 1},
                    html2canvas: {
                        useCORS: true,
                        scale: 2,
                    },
                })
                .from(element)
                .save()
                .then(() =>
                    showNotification(
                        severityNotification.success,
                        'Export PDF file successfully !',
                    ),
                )
        }
    }, [pdfData])

    const handleOnSelectAdvance = useCallback(
        key => {
            let listChange = [...excludesColumnAdvance]

            if (excludesColumnAdvance.length > 0) {
                if (excludesColumnAdvance.includes(key)) {
                    listChange = listChange.filter(item => item !== key)
                    setExcludeColumnAdvance([...listChange])
                } else {
                    setExcludeColumnAdvance([...listChange, key])
                }
            } else {
                setExcludeColumnAdvance([key])
            }
        },
        [excludesColumnAdvance],
    )

    const renderEditColumn = useCallback(() => {
        return (
            <div className="flex flex-column gap-1">
                {dataColumns.map((item, idx) => {
                    let isExcludes = excludesColumnAdvance.includes(item.key)
                    return (
                        <Button
                            key={idx}
                            className={`w-full justify-content-between font-bold ${
                                isExcludes &&
                                'bg-white text-green hover:bg-green-50 '
                            }`}
                            onClick={() => handleOnSelectAdvance(item.key)}>
                            <span>
                                {translateKeys(convertKeyToLabel(item.key))}
                            </span>
                            {!isExcludes && (
                                <i className={`pi pi-check-circle`}></i>
                            )}
                        </Button>
                    )
                })}
            </div>
        )
    }, [excludesColumnAdvance, translateKeys, handleOnSelectAdvance])

    const overlayRef = useRef(null)
    const rowDataRef = useRef(null)

    return (
        <div>
            <Sidebar
                className="h-full"
                blockScroll
                visible={visible}
                onHide={() => {
                    setVisible(false)
                    setIsReviewPDF(false)
                }}
                style={{
                    width: '100vw',
                }}>
                <FormHeader
                    header="REPORT"
                    onHide={() => {
                        setVisible(false)
                        setIsReviewPDF(false)
                        overlayRef.current.hide()
                        rowDataRef.current = null
                    }}
                />
                <motion.div
                    initial={{x: 300}}
                    animate={{x: 0}}
                    transition={{type: 'spring'}}>
                    {!isReviewPDF ? (
                        <motion.div
                            initial={{x: 300}}
                            animate={{x: 0}}
                            transition={{type: 'spring'}}
                            className="grid gap-2">
                            <DataTable
                                scrollable
                                resizableColumns
                                dragSelection
                                paginator={false}
                                rows={5}
                                className="flex-1 overflow-x-auto"
                                scrollHeight="auto"
                                columnResizeMode="fit"
                                header={
                                    <div>
                                        <h3 className="mb-0 font-bold text-xl sm:text-xl md:text-3xl lg:text-3xl">
                                            {translateDatatable(
                                                'Patient Results',
                                            )}
                                        </h3>
                                        <small>
                                            {translateCommons(
                                                'The patient list is found based on a dynamic query in the system. You can export patient information into PDF or Excel files here!',
                                            )}
                                        </small>
                                    </div>
                                }
                                lazy={false}
                                value={queryData ? queryData : []}
                                stripedRows
                                ref={datatableRef}
                                responsiveLayout="scroll">
                                <Column
                                    header={translateCommons('Index')}
                                    style={{
                                        maxWidth: '50px',
                                    }}
                                    body={rowData => (
                                        <Badge
                                            value={
                                                queryData.indexOf(rowData) + 1
                                            }></Badge>
                                    )}
                                />
                                <Column
                                    header={translateCommons('View')}
                                    style={{
                                        minWidth: '180px',
                                    }}
                                    body={rowData => (
                                        <span
                                            className="cursor-pointer text-darkcyan font-bold"
                                            onClick={e => {
                                                history.push(
                                                    `/patients/${rowData.id}`,
                                                )
                                            }}>
                                            <i className="pi pi-file-export mr-2"></i>
                                            <span>View Information</span>
                                        </span>
                                    )}
                                />
                                <Column
                                    header={translateCommons('Export')}
                                    body={rowData => (
                                        <span
                                            className="cursor-pointer text-darkcyan font-bold"
                                            onClick={e => {
                                                overlayRef.current?.hide(e)
                                                overlayRef.current?.toggle(e)
                                                rowDataRef.current = rowData
                                            }}>
                                            <i className="pi pi-file-export mr-2"></i>
                                            <span>Export file</span>
                                        </span>
                                    )}
                                />
                                {dataLabelColumns.map(item => {
                                    return (
                                        <Column
                                            field={item.key}
                                            style={{
                                                minWidth: '120px',
                                            }}
                                            body={rowData => {
                                                if (
                                                    item?.key === 'active' ||
                                                    item?.key === 'deleted' ||
                                                    item?.key === 'assigned'
                                                ) {
                                                    return !!rowData[
                                                        item.key
                                                    ] ? (
                                                        <Tag
                                                            className="px-3"
                                                            severity="success"
                                                            value={translateDatatable(
                                                                'Yes',
                                                            )}
                                                        />
                                                    ) : (
                                                        <Tag
                                                            severity="danger"
                                                            value={translateDatatable(
                                                                'No',
                                                            )}
                                                            className="px-3"
                                                        />
                                                    )
                                                } else {
                                                    if (
                                                        item.key ===
                                                        'identityWithHeldReason'
                                                    ) {
                                                        return !!rowData[
                                                            'identityWithHeldReason'
                                                        ] ? (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {!!identityWithHeldReasonList?.find(
                                                                    item =>
                                                                        item?.value ===
                                                                        rowData[
                                                                            'identityWithHeldReason'
                                                                        ],
                                                                )
                                                                    ? translateOptions(
                                                                          identityWithHeldReasonList,
                                                                      )?.find(
                                                                          item =>
                                                                              item?.value ===
                                                                              rowData[
                                                                                  'identityWithHeldReason'
                                                                              ],
                                                                      )?.label
                                                                    : '-'}
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )
                                                    } else if (
                                                        item.key === 'religion'
                                                    ) {
                                                        return rowData[
                                                            'religion'
                                                        ] ? (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {!!religionList.find(
                                                                    item =>
                                                                        item?.value ===
                                                                        rowData[
                                                                            'religion'
                                                                        ],
                                                                )
                                                                    ? translateOptions(
                                                                          religionList,
                                                                      )?.find(
                                                                          item =>
                                                                              item?.value ===
                                                                              rowData[
                                                                                  'religion'
                                                                              ],
                                                                      ).label
                                                                    : '-'}
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )
                                                    } else if (
                                                        item.key ===
                                                        'diagnostic'
                                                    ) {
                                                        return rowData[
                                                            'diagnostic'
                                                        ] ? (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {!!diagnosticList?.find(
                                                                    item =>
                                                                        item?.value ===
                                                                        rowData[
                                                                            'diagnostic'
                                                                        ],
                                                                )
                                                                    ? translateOptions(
                                                                          diagnosticList,
                                                                      )?.find(
                                                                          item =>
                                                                              item?.value ===
                                                                              rowData[
                                                                                  'diagnostic'
                                                                              ],
                                                                      ).label
                                                                    : '-'}
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )
                                                    } else if (
                                                        item.key ===
                                                        'accommodationStatus'
                                                    ) {
                                                        return rowData[
                                                            'accommodationStatus'
                                                        ] ? (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {!!accommodationStatusList?.find(
                                                                    item =>
                                                                        item?.value ===
                                                                        rowData[
                                                                            'accommodationStatus'
                                                                        ],
                                                                )
                                                                    ? translateOptions(
                                                                          accommodationStatusList,
                                                                      )?.find(
                                                                          item =>
                                                                              item?.value ===
                                                                              rowData[
                                                                                  'accommodationStatus'
                                                                              ],
                                                                      ).label
                                                                    : '-'}
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )
                                                    } else if (
                                                        item.key ===
                                                        'maritalStatus'
                                                    ) {
                                                        return rowData[
                                                            'maritalStatus'
                                                        ] ? (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {!!materialStatusList.find(
                                                                    item =>
                                                                        item?.value ===
                                                                        rowData[
                                                                            'maritalStatus'
                                                                        ],
                                                                )
                                                                    ? translateOptions(
                                                                          materialStatusList,
                                                                      )?.find(
                                                                          item =>
                                                                              item?.value ===
                                                                              rowData[
                                                                                  'maritalStatus'
                                                                              ],
                                                                      ).label
                                                                    : '-'}
                                                            </div>
                                                        ) : (
                                                            '-'
                                                        )
                                                    } else if (
                                                        rowData[item.key]
                                                    ) {
                                                        return (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    width: '100%',
                                                                }}>
                                                                {
                                                                    rowData[
                                                                        item.key
                                                                    ]
                                                                }
                                                            </div>
                                                        )
                                                    } else {
                                                        return (
                                                            <div
                                                                className="overflow-dot overflow-text-2"
                                                                style={{
                                                                    textTransform:
                                                                        'capitalize',
                                                                }}>
                                                                {'-'}
                                                            </div>
                                                        )
                                                    }
                                                }
                                            }}
                                            key={item.key}
                                            header={translateKeys(
                                                item.header,
                                            )}></Column>
                                    )
                                })}
                            </DataTable>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{x: 300}}
                            animate={{x: 0}}
                            transition={{type: 'spring'}}>
                            <div className="flex align-items-center justify-content-between">
                                <div>
                                    <h3 className="font-bold mb-1">
                                        {translateCommons(
                                            "Review Patient's Information PDF",
                                        )}
                                    </h3>
                                    <div>
                                        <p>
                                            {translateCommons(
                                                'Please review the patient information before exporting the PDF file.',
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex align-items-center gap-2">
                                    <span
                                        className="cursor-pointer px-3"
                                        onClick={() => {
                                            handleOnPrintPDF()
                                        }}>
                                        <i className="pi pi-print mr-2"></i>
                                        <span>
                                            {translateCommons(
                                                'Print this profile',
                                            )}
                                        </span>
                                    </span>

                                    <span
                                        className="cursor-pointer"
                                        onClick={() => {
                                            setIsReviewPDF(false)
                                        }}>
                                        <i className="pi pi-arrow-left mr-2"></i>
                                        <span>
                                            {translateCommons(
                                                'Back to Patient Results',
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </div>
                            {/* Element For Print */}
                            <div ref={pdfElement}>
                                <PdfContent exportData={pdfData} />
                            </div>
                        </motion.div>
                    )}
                </motion.div>
                <OverlayPanel ref={overlayRef}>
                    <div className="flex flex-column align-items-center justify-content-center gap-2">
                        <Button
                            className="bg-orange-400 border-none w-full"
                            type="button"
                            icon="pi pi-file-pdf"
                            label={translateDatatable('PDF')}
                            onClick={() => {
                                onExport(types.pdf, rowDataRef.current)
                                overlayRef.current.hide()
                            }}
                        />
                        <Button
                            className="bg-green-400 border-none w-full"
                            type="button"
                            icon="pi pi-file-excel"
                            label={translateDatatable('Excel')}
                            onClick={() => {
                                onExport(types.excel, rowDataRef.current)
                                overlayRef.current.hide()
                            }}
                        />
                    </div>
                </OverlayPanel>
            </Sidebar>
            <ToolBoxWrapper
                zIndex={6000}
                header={translateDatatable('Edit Column')}
                ref={editColumnModalSheetRef}>
                <div className="overflow-scroll py-3" style={{height: '64vh'}}>
                    {renderEditColumn()}
                </div>
            </ToolBoxWrapper>
        </div>
    )
})

export default ReportTableResult
